import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { listPsmfs } from '@/components/psmf/storage';
import type { Psmf } from '@/components/psmf/types';

export function PsmfListPage() {
  const user = useAuthStore((s) => s.user);
  const [psmfs, setPsmfs] = useState<Psmf[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    setPsmfs(listPsmfs(user.tenantId));
  }, [user]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pharmacovigilance System Master File</h1>
          <p className="text-sm text-muted-foreground">
            EU GVP Module II Section II.B.4 main body and Section II.B.6.1 Annex A-I structure.
          </p>
        </div>
        <Button onClick={() => navigate('/psmf/new')}>
          <Plus className="mr-1.5 h-4 w-4" /> Create PSMF
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Master files</CardTitle>
          <CardDescription>
            Only one active PSMF is typically held per MAH tenant. Draft revisions may be kept in parallel.
            Back-end persistence: add /api/psmf routes — PSMFs are currently held in browser storage.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>QPPV</TableHead>
                <TableHead>EV PSMF ref</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last updated</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {psmfs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center">
                    <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-8 w-8 opacity-50" />
                      <div>No PSMFs yet.</div>
                      <Button size="sm" onClick={() => navigate('/psmf/new')}>
                        <Plus className="mr-1 h-4 w-4" /> Create your first PSMF
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {psmfs.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <Link to={`/psmf/${p.id}`} className="font-medium hover:underline">
                      v{p.cover.versionNumber || '—'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{p.cover.qppvName || <span className="text-muted-foreground">not set</span>}</TableCell>
                  <TableCell className="text-xs font-mono">{p.cover.evPsmfRef || <span className="text-muted-foreground">--</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(p.createdAt)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(p.updatedAt)}</TableCell>
                  <TableCell>
                    <Badge variant={p.status === 'FINALISED' ? 'success' : 'warning'}>{p.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
